import { Download, Share, X } from "lucide-react"
import { useInstallPrompt, useMeaningfulActionTrigger } from "@/hooks/use-install-prompt"
import { translate } from "@/lib/i18n/translate"
import { Button } from "@/components/ui/button"

interface InstallBannerProps {
  trigger?: boolean
}

export function InstallBanner({ trigger }: InstallBannerProps) {
  const meaningful = useMeaningfulActionTrigger()
  const showTrigger = trigger ?? meaningful
  const {
    canInstall,
    isIOS,
    isInstalled,
    isIOSDismissed,
    isInstallDismissed,
    promptInstall,
    dismissIOS,
    dismissInstall,
  } = useInstallPrompt()

  if (isInstalled) return null
  if (!showTrigger) return null

  if (isIOS && !isIOSDismissed) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-[54] mx-auto max-w-lg rounded-lg border border-primary/40 bg-background/95 p-4 shadow-lg backdrop-blur md:bottom-4">
        <div className="flex items-start gap-3">
          <Share className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
          <div className="flex-1 space-y-1">
            <p className="text-sm font-medium">{translate("pwa.installTitle")}</p>
            <p className="text-xs text-muted-foreground">{translate("pwa.iosInstructions")}</p>
          </div>
          <button
            type="button"
            onClick={dismissIOS}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
            aria-label={translate("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    )
  }

  if (canInstall && !isInstallDismissed) {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-[54] mx-auto max-w-lg rounded-lg border border-primary/40 bg-background/95 p-4 shadow-lg backdrop-blur md:bottom-4">
        <div className="flex items-center gap-3">
          <Download className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          <p className="flex-1 text-sm">{translate("pwa.installPrompt")}</p>
          <Button size="sm" onClick={() => promptInstall()}>
            {translate("pwa.install")}
          </Button>
          <button
            type="button"
            onClick={dismissInstall}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
            aria-label={translate("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    )
  }

  return null
}
